import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { CreditsSection } from "@/components/settings/CreditsSection";
import { EnrichmentRatesSection } from "@/components/settings/EnrichmentRatesSection";
import { FeaturesSection } from "@/components/settings/FeaturesSection";
import { FiltersSection } from "@/components/settings/FiltersSection";
import { MembersSection } from "@/components/settings/MembersSection";
import { ProjectSection } from "@/components/settings/ProjectSection";
import { PipelineSection } from "@/components/settings/PipelineSection";
import { ScoringSection } from "@/components/settings/ScoringSection";
import { SearchCriteriaSection } from "@/components/settings/SearchCriteriaSection";
import { TaskStatusesSection } from "@/components/settings/TaskStatusesSection";
import { RolesSection } from "@/components/settings/RolesSection";
import { SuperadminsSection } from "@/components/settings/SuperadminsSection";
import { TagManager } from "@/components/tags/TagManager";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { project, isSuperadmin } = useCurrentProject();
  const perms = usePermissions();
  const scoringEnabled = useFeature("scoring");
  const enrichmentEnabled = useFeature("enrichment");
  const slug = project?.slug;
  // RBAC + feature flag. Toggling Enrichment off in Settings →
  // Features hides the rate-card tab so the operator can't edit
  // rates for a workspace that's been disabled.
  const enrichmentRatesVisible =
    !!project && perms.canManageEnrichmentRates && enrichmentEnabled;
  // The provider accounts a project pays with (the CFO engine's, M&A's Lusha and Kaspr),
  // for whoever sees the engine.
  const creditsVisible =
    !!project &&
    perms.canViewEngine &&
    ("_cfo_workflow" in (project.config ?? {}) || project.project_type_id === "m_and_a");

  useEffect(() => {
    if (slug && !perms.canViewSettings) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, perms.canViewSettings, navigate]);

  if (!perms.canViewSettings || !project) {
    return null;
  }

  return (
    <div className="space-y-6 pb-12">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Settings
        </h1>
        <p className="text-sm text-slate-500">{project.name}</p>
      </header>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Members</TabsTrigger>
          {perms.canManageRoles && (
            <TabsTrigger value="roles">Roles</TabsTrigger>
          )}
          {perms.canManageTags && (
            <TabsTrigger value="tags">Tags</TabsTrigger>
          )}
          {isSuperadmin && (
            <TabsTrigger value="superadmins">Superadmins</TabsTrigger>
          )}
          {isSuperadmin && (
            <TabsTrigger value="features">Features</TabsTrigger>
          )}
          <TabsTrigger value="project">Project</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="task-statuses">Task statuses</TabsTrigger>
          {scoringEnabled && (
            <TabsTrigger value="scoring">Scoring</TabsTrigger>
          )}
          {enrichmentRatesVisible && (
            <TabsTrigger value="enrichment_rates">
              Enrichment Rates
            </TabsTrigger>
          )}
          {creditsVisible && <TabsTrigger value="credits">Credits</TabsTrigger>}
        </TabsList>

        <TabsContent value="members" className="mt-6">
          <MembersSection projectSlug={project.slug} />
        </TabsContent>

        {perms.canManageRoles && (
          <TabsContent value="roles" className="mt-6">
            <RolesSection projectSlug={project.slug} />
          </TabsContent>
        )}

        {perms.canManageTags && (
          <TabsContent value="tags" className="mt-6">
            <TagManager projectSlug={project.slug} />
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="superadmins" className="mt-6">
            <SuperadminsSection />
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="features" className="mt-6">
            <FeaturesSection project={project} />
          </TabsContent>
        )}

        <TabsContent value="project" className="mt-6">
          <ProjectSection project={project} />
        </TabsContent>

        <TabsContent value="pipeline" className="mt-6">
          <PipelineSection project={project} />
        </TabsContent>

        <TabsContent value="task-statuses" className="mt-6">
          <TaskStatusesSection project={project} />
        </TabsContent>

        {scoringEnabled && (
          <TabsContent value="scoring" className="mt-6">
            {hasMaConfig(project) ? (
              <Tabs defaultValue="rules">
                <TabsList>
                  <TabsTrigger value="rules">Scoring Rules</TabsTrigger>
                  <TabsTrigger value="search-criteria">
                    Search Criteria
                  </TabsTrigger>
                  <TabsTrigger value="filters">Filters</TabsTrigger>
                </TabsList>
                <TabsContent value="rules" className="mt-6">
                  <ScoringSection project={project} />
                </TabsContent>
                <TabsContent value="search-criteria" className="mt-6">
                  <SearchCriteriaSection project={project} />
                </TabsContent>
                <TabsContent value="filters" className="mt-6">
                  <FiltersSection project={project} />
                </TabsContent>
              </Tabs>
            ) : (
              <ScoringSection project={project} />
            )}
          </TabsContent>
        )}

        {enrichmentRatesVisible && (
          <TabsContent value="enrichment_rates" className="mt-6">
            <EnrichmentRatesSection project={project} />
          </TabsContent>
        )}

        {creditsVisible && (
          <TabsContent value="credits" className="mt-6">
            <CreditsSection
              project={project}
              canCheck={perms.canTriggerEngine}
              canSendTest={perms.canConfigureEngine}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/** True when the project ships an M&A-style config — the Scoring tab
 *  then renders sub-tabs for the M&A-specific surfaces. Gates on
 *  `search_criteria` rather than `project_type_id` so a CFO project
 *  that also gets enriched via Pappers picks up the same UX. */
function hasMaConfig(project: { config: Record<string, unknown> }): boolean {
  const sc = project.config?.search_criteria;
  return !!sc && typeof sc === "object";
}
